import { ReactElement, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import {
    UIEmptyState,
    UIErrorState,
    UIFilterGroup,
    UIFormInput,
    UIIcon,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIMultiFilterGroup,
    UIMultiFilterItem,
    UIPopover,
    UIPressableAreaButtonLess,
    UIPropoverRef,
    UISection,
    UITableHeaderCell,
    UITableSortDirection,
    UITagGroup,
    UIText,
    useUIConfirmationModal
} from '@/ui';
import styles from './beacons-list.module.scss';
import { Beacon, BeaconBattery } from '@/api';
import { charcoal, red } from '@/ui/styles';
import { useFormatDate } from '@/common/hooks';
import { UpdateBeaconModal } from './update-beacon-modal';
import { useDeleteBeaconAction } from '../actions';
import { useBeacons } from '../providers';
import { BeaconStatus } from './beacon-status';
import { search } from '@/common/util/search';
import { useDevicesTranslation } from '../hooks';

type SortBy = 'name' | 'tags' | 'addedAt';

export function BeaconsList(): ReactElement {
    const { beacons, status, refetch, availableTags } = useBeacons();
    const { t } = useDevicesTranslation();
    return (
        <>
            {status === 'pending' && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 200
                    }}
                >
                    <UILoadingState />
                </div>
            )}
            {status === 'error' && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 200
                    }}
                >
                    <UIErrorState
                        title={t('components.beacons_list.error_title')}
                        onRetry={refetch}
                    />
                </div>
            )}
            {status === 'success' && beacons !== undefined && (
                <LoadedBeaconsList
                    beacons={beacons}
                    availableTags={availableTags}
                />
            )}
        </>
    );
}
export function LoadedBeaconsList(props: {
    beacons: Beacon[];
    availableTags: string[];
}): ReactElement {
    const { beacons, availableTags } = props;
    const { t } = useDevicesTranslation();

    const [searchQuery, setSearchQuery] = useState('');
    type BatteryFilter = 'all' | 'low' | 'good';
    const [batteryFilter, setBatteryFilter] = useState<BatteryFilter>('all');
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const selectedCount = useMemo(() => {
        let count = 0;
        if (batteryFilter !== 'all') {
            count++;
        }

        count += selectedTags.size;

        return count;
    }, [batteryFilter, selectedTags]);
    function clearSearch() {
        setSearchQuery('');
        setSelectedTags(new Set());
        setBatteryFilter('all');
    }

    const groups: UIMultiFilterGroup[] = useMemo(() => {
        return [
            {
                name: t('shared.filters.battery'),
                type: 'single',
                items: [
                    {
                        name: t('shared.filters.battery_all'),
                        id: 'all',
                        selected: batteryFilter === 'all'
                    },
                    {
                        name: t('shared.filters.battery_low'),
                        id: 'low',
                        selected: batteryFilter === 'low'
                    },
                    {
                        name: t('shared.filters.battery_good'),
                        id: 'good',
                        selected: batteryFilter === 'good'
                    }
                ]
            },
            {
                name: t('components.beacons_list.tags'),
                emptyText: t('components.beacons_list.no_tags'),
                type: 'multi',
                items: availableTags.map((tag) => ({
                    name: tag,
                    id: tag,
                    selected: selectedTags.has(tag)
                }))
            }
        ];
    }, [batteryFilter, selectedTags, availableTags, t]);

    const filteredBeacons = useMemo(() => {
        const selectedTagsArray = Array.from(selectedTags);
        return beacons.filter((beacon) => {
            if (batteryFilter === 'low') {
                if (!beacon.battery || !isBatteryLow(beacon.battery)) {
                    return false;
                }
            }
            if (batteryFilter === 'good') {
                if (!beacon.battery || !isBatteryGood(beacon.battery)) {
                    return false;
                }
            }

            if (selectedTags.size > 0) {
                if (
                    !selectedTagsArray.every((tag) => beacon.tags.includes(tag))
                ) {
                    return false;
                }
            }

            return true;
        });
    }, [batteryFilter, beacons, selectedTags]);

    const searchedBeacons = useMemo(() => {
        return search(searchQuery, filteredBeacons, [
            { name: 'mac', threshold: 0.0 }, // Perfect match only
            'name',
            'tags'
        ]);
    }, [filteredBeacons, searchQuery]);

    const [sortBy, setSortBy] = useState<SortBy>('name');
    const [sortDirection, setSortDirection] =
        useState<UITableSortDirection>('asc');

    const sortedBeacons = useMemo(() => {
        return searchedBeacons.sort((a, b) => {
            if (sortBy === 'name') {
                if (sortDirection === 'asc') {
                    return a.name.localeCompare(b.name);
                } else {
                    return b.name.localeCompare(a.name);
                }
            }

            if (sortBy === 'addedAt') {
                const teamA = new Date(a.createdAt).getTime();
                const teamB = new Date(b.createdAt).getTime();
                if (sortDirection === 'asc') {
                    return teamA - teamB;
                } else {
                    return teamB - teamA;
                }
            }

            return 0;
        });
    }, [searchedBeacons, sortBy, sortDirection]);

    if (beacons.length === 0) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 400,
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 16
                }}
            >
                <UIEmptyState
                    title={t('components.beacons_list.empty_title')}
                    description={t('components.beacons_list.empty_description')}
                />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                <UIFilterGroup>
                    <UIMultiFilterItem
                        icon="sliders"
                        text={t('shared.labels.filter')}
                        groups={groups}
                        selectedCount={selectedCount}
                        onChanged={(newGroups) => {
                            const newSelectedBatteryFilter =
                                newGroups[0].items.find((item) => item.selected)
                                    ?.id as BatteryFilter;
                            setBatteryFilter(newSelectedBatteryFilter);

                            const newSelectedTags = new Set<string>();
                            newGroups[1].items.forEach((item) => {
                                if (item.selected) {
                                    newSelectedTags.add(item.name);
                                }
                            });
                            setSelectedTags(newSelectedTags);
                        }}
                    />
                </UIFilterGroup>
                <UIFormInput
                    value={searchQuery}
                    placeholder={t('shared.labels.search')}
                    height={20}
                    onChange={setSearchQuery}
                />
            </div>
            {sortedBeacons.length === 0 && (
                <UIEmptyState
                    description={t('shared.labels.no_results')}
                    buttonText={t('shared.labels.clear_search')}
                    emoji="😔"
                    onClick={clearSearch}
                />
            )}
            {sortedBeacons.length > 0 && (
                <UISection>
                    <table className={styles.beaconTable}>
                        <thead>
                            <BeaconTableHeader
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                onClick={(newSortBy, newSortDirection) => {
                                    setSortBy(newSortBy);
                                    setSortDirection(newSortDirection);
                                }}
                            />
                        </thead>
                        <tbody>
                            {sortedBeacons.map((beacon) => (
                                <BeaconTableRow
                                    key={beacon.id}
                                    beacon={beacon}
                                    availableTags={availableTags}
                                />
                            ))}
                        </tbody>
                    </table>
                </UISection>
            )}
        </div>
    );
}

function BeaconTableHeader(props: {
    sortBy: SortBy;
    sortDirection: UITableSortDirection;
    onClick: (sortBy: SortBy, sortDirection: UITableSortDirection) => void;
}): ReactElement {
    const { sortBy, sortDirection, onClick } = props;
    const { t } = useDevicesTranslation();
    return (
        <tr>
            <UITableHeaderCell
                text={t('shared.fields.name.label')}
                sortable
                sortDirection={sortBy === 'name' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('name', newSortDirection);
                }}
            />
            <UITableHeaderCell
                text={t('components.beacons_list.tags')}
                tooltip={t('components.beacons_list.column_tags_tooltip')}
            />
            <UITableHeaderCell
                text={t('shared.labels.status')}
                tooltip={t('components.beacons_list.column_status_tooltip')}
            />
            <UITableHeaderCell
                text={t('components.beacons_list.column_added_at')}
                sortable
                sortDirection={sortBy === 'addedAt' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('addedAt', newSortDirection);
                }}
            />
            <th />
        </tr>
    );
}

function BeaconTableRow(props: {
    beacon: Beacon;
    availableTags: string[];
}): ReactElement {
    const { beacon, availableTags } = props;
    const { t } = useDevicesTranslation();
    const { formatDateOnly } = useFormatDate();

    const popoverRef = useRef<UIPropoverRef>(null);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

    const { deleteBeacon, loading } = useDeleteBeaconAction({
        beaconId: beacon.id
    });

    const confirmationModal = useUIConfirmationModal();

    return (
        <>
            <tr className={styles.tableRow}>
                <td>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4
                        }}
                    >
                        <UIText medium>{beacon.name}</UIText>
                        <UIText variant="small" type="default">
                            <Trans
                                t={t}
                                i18nKey="components.beacons_list.mac"
                                values={{ mac: beacon.mac }}
                                components={{
                                    v: (
                                        <UIText
                                            variant="small"
                                            type="default2"
                                        />
                                    )
                                }}
                            />
                        </UIText>
                    </div>
                </td>
                <td className={styles.flexCell}>
                    <div className={styles.tagsCell}>
                        <UITagGroup
                            tags={beacon.tags.map((tag) => ({ text: tag }))}
                        />
                        {beacon.tags.length === 0 && (
                            <UIText type={'default2'}>
                                {t('components.beacons_list.no_tags')}
                            </UIText>
                        )}
                    </div>
                </td>
                <td>
                    <StatusCell beacon={beacon} />
                </td>
                <td>
                    <UIText type="default2">
                        {formatDateOnly(new Date(beacon.createdAt))}
                    </UIText>
                </td>
                <td>
                    <UIPopover
                        ref={popoverRef}
                        placement="bottom_left"
                        popoverPadding={4}
                        trigger="click"
                        triggerNode={
                            <button className={styles.removeButtonStyle}>
                                <UIPressableAreaButtonLess
                                    width={16}
                                    height={16}
                                >
                                    <UIIcon
                                        name={'more-vertical'}
                                        size={16}
                                        color={charcoal}
                                    />
                                </UIPressableAreaButtonLess>
                            </button>
                        }
                    >
                        <UIMenu>
                            <UIMenuItem
                                text={t('shared.labels.update')}
                                leftIconName="edit-2"
                                onClick={() => {
                                    popoverRef.current?.close();
                                    setIsUpdateModalOpen(true);
                                }}
                            />
                            <UIMenuItem
                                text={t('shared.labels.delete')}
                                leftIconName="trash-2"
                                loading={loading}
                                textColor={red}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    popoverRef.current?.close();
                                    confirmationModal.open({
                                        title: t(
                                            'components.beacons_list.delete_confirm_title'
                                        ),
                                        confirmText: t(
                                            'components.beacons_list.delete_confirm'
                                        ),
                                        confirmVariant: 'danger',
                                        cancelText: t('shared.labels.cancel'),
                                        onConfirm: () => {
                                            confirmationModal.setLoading(true);
                                            deleteBeacon()
                                                .finally(() => {
                                                    confirmationModal.close();
                                                })
                                                .catch(() => {}); // Error handled in the action
                                        },
                                        onCancel: () => {
                                            confirmationModal.close();
                                        }
                                    });
                                }}
                            />
                        </UIMenu>
                    </UIPopover>
                </td>
            </tr>
            <UpdateBeaconModal
                beacon={beacon}
                availableTags={availableTags}
                isOpen={isUpdateModalOpen}
                onClose={() => setIsUpdateModalOpen(false)}
            />
        </>
    );
}

function StatusCell(props: { beacon: Beacon }): ReactElement {
    const { beacon } = props;

    return <BeaconStatus beacon={beacon} />;
}

function isBatteryGood(battery: BeaconBattery): boolean {
    return battery.level >= 10;
}

function isBatteryLow(battery: BeaconBattery): boolean {
    return battery.level < 10;
}
